import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ENVIRONMENT } from 'shared';
import {
  CardPartnerAccount,
  CardPartnerCard,
  CardPartnerTransaction,
  CardPartnerTransfer
} from './mycards.service';

/**
 * Admin analytics over the card partner data mirrored into our DB
 * (Api::AdminCardPartnerController). Money comes as decimal strings; deposits and
 * withdrawals stay per token (USDT/USDC are not converted). `month` is `YYYY-MM`
 * or `all`.
 */

export interface CardPartnerPeriod {
  month: string;
  from: string | null;
  to: string | null;
}

export interface CardPartnerTypeStatusRow {
  type: string | null;
  status: string | null;
  count: number;
  volume: string;
  fees: string;
}

export interface CardPartnerTransferRow {
  chain?: string | null;
  status?: string | null;
  currency: string | null;
  count: number;
  volume: string;
  fees?: string;
}

export interface CardPartnerTransferSummary {
  count: number;
  settled_by_token: CardPartnerTransferRow[];
  refunded_by_token: CardPartnerTransferRow[];
  by_chain_token: CardPartnerTransferRow[];
  by_status: CardPartnerTransferRow[];
}

export interface CardPartnerSeriesPoint {
  /** `YYYY-MM` in the monthly series. */
  month?: string;
  /** `YYYY-MM-DD` in the daily series. */
  day?: string;
  spend: string;
  fees: string;
  deposits: string;
  activations: number;
  new_accounts: number;
}

export interface CardPartnerOverview {
  period: CardPartnerPeriod;
  accounts: {
    total: number;
    new_in_period: number;
    linked: number;
    unlinked: number;
    by_kyc_status: Record<string, number>;
    by_account_status: Record<string, number>;
  };
  cards: {
    total: number;
    created_in_period: number;
    activations_in_period: number;
    activated_total: number;
    by_card_status: Record<string, number>;
    by_physical_status: Record<string, number>;
  };
  transactions: {
    count: number;
    by_type_status: CardPartnerTypeStatusRow[];
    spend_volume: string;
    spend_count: number;
    fee_volume: string;
    average_ticket: string | null;
    failed_count: number;
    pending_count: number;
    top_categories: { category: string | null; count: number; volume: string }[];
  };
  deposits: CardPartnerTransferSummary;
  withdrawals: CardPartnerTransferSummary;
  series: CardPartnerSeriesPoint[];
  /** Last 30 days ending with the selected month (or today). */
  daily_series: CardPartnerSeriesPoint[];
  last_synced_at: string | null;
}

export interface CardPartnerTokenAmounts {
  USDT: string;
  USDC: string;
  total: string;
}

export interface CardPartnerRankingRow {
  partner_user_id: string;
  email: string | null;
  nickname: string | null;
  user: { id: number; email: string | null } | null;
  kyc_status: string | null;
  account_status: string | null;
  card_status: string | null;
  cards_count: number;
  card_activated_at: string | null;
  partner_created_at: string | null;
  spend: string;
  fees: string;
  tx_count: number;
  last_tx_at: string | null;
  deposits: CardPartnerTokenAmounts;
  withdrawals: CardPartnerTokenAmounts;
}

export interface CardPartnerRankingResponse {
  period: CardPartnerPeriod;
  items: CardPartnerRankingRow[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
  sort: string;
  dir: 'ASC' | 'DESC';
}

export interface CardPartnerRankingQuery {
  month: string;
  sort?: string;
  dir?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
  q?: string;
  kyc_status?: string;
  has_card?: '' | 'yes' | 'no';
  linked?: '' | 'yes' | 'no';
  active?: boolean;
}

export interface CardPartnerAdminAccount extends CardPartnerAccount {
  partner_user_id: string;
  user_id: number | null;
  email: string | null;
  account_id: string | null;
  card_holder_id: string | null;
  referral_code: string | null;
  referrer_id: string | null;
  nickname: string | null;
}

export interface CardPartnerAdminCard extends CardPartnerCard {
  synced_at: string | null;
}

export interface CardPartnerAdminAddress {
  network: string;
  token: string;
  address: string | null;
  memo: string | null;
  registered: boolean;
  first_registered_at: string | null;
}

export interface CardPartnerAccountTotals {
  spend: string;
  fees: string;
  tx_count: number;
  deposits: Record<string, string>;
  withdrawals: Record<string, string>;
  withdrawal_fees: string;
}

export interface CardPartnerAdminDetail {
  period: CardPartnerPeriod;
  account: CardPartnerAdminAccount;
  user: { id: number; email: string; full_name: string | null; domain_id: number } | null;
  referrer: { partner_user_id: string; email: string | null; user_id: number | null } | null;
  cards: CardPartnerAdminCard[];
  deposit_addresses: CardPartnerAdminAddress[];
  totals: { period: CardPartnerAccountTotals; all_time: CardPartnerAccountTotals };
  counts: { transactions: number; deposits: number; withdrawals: number };
  refresh_error?: string;
}

export interface CardPartnerAdminTransaction extends CardPartnerTransaction {
  mcc: string | null;
  mcc_category: string | null;
  cashback_eligible: boolean | null;
}

export interface CardPartnerAdminTransfer extends CardPartnerTransfer {
  from_address?: string | null;
  ladder_status?: string | null;
}

export interface CardPartnerAdminPage<T> {
  items: T[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export type CardPartnerAdminListKind = 'transactions' | 'deposits' | 'withdrawals';

@Injectable({ providedIn: 'root' })
export class AdminCardPartnerService {
  private readonly apiUrl = this.environment.apiUrl || 'http://localhost:3000/api';
  private readonly base = `${this.apiUrl}/admin/card_partner`;

  constructor(private http: HttpClient, @Inject(ENVIRONMENT) private environment: any) {}

  overview(month: string): Observable<CardPartnerOverview> {
    return this.http.get<CardPartnerOverview>(`${this.base}/overview`, { params: { month } });
  }

  users(query: CardPartnerRankingQuery): Observable<CardPartnerRankingResponse> {
    return this.http.get<CardPartnerRankingResponse>(`${this.base}/users`, { params: this.rankingParams(query) });
  }

  /** CSV of the ranking with the same filters, every row. */
  exportUsers(query: CardPartnerRankingQuery): Observable<string> {
    const params = this.rankingParams({ ...query, page: undefined, per_page: undefined });
    return this.http.get(`${this.base}/users/export`, { params, responseType: 'text' });
  }

  detail(partnerUserId: string, month: string): Observable<CardPartnerAdminDetail> {
    return this.http.get<CardPartnerAdminDetail>(`${this.base}/users/${encodeURIComponent(partnerUserId)}`, { params: { month } });
  }

  list<T>(partnerUserId: string, kind: CardPartnerAdminListKind, page: number, perPage = 50): Observable<CardPartnerAdminPage<T>> {
    return this.http.get<CardPartnerAdminPage<T>>(
      `${this.base}/users/${encodeURIComponent(partnerUserId)}/${kind}`,
      { params: { page: String(page), per_page: String(perPage) } }
    );
  }

  refresh(partnerUserId: string, month: string): Observable<CardPartnerAdminDetail> {
    return this.http.post<CardPartnerAdminDetail>(
      `${this.base}/users/${encodeURIComponent(partnerUserId)}/refresh`, {}, { params: { month } }
    );
  }

  private rankingParams(query: CardPartnerRankingQuery): HttpParams {
    let params = new HttpParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '' || value === false) return;
      params = params.set(key, value === true ? '1' : String(value));
    });
    return params;
  }
}
